/**
 * Learner authentication for learn.4klabs.ai.
 *
 * A SECOND, NAMED Firebase app pointed at the `fourklabs-learn` project — the
 * studio app in `firebase.ts` is left completely alone. Two consequences worth
 * stating plainly:
 *
 *  - A learner token cannot authenticate a studio request and vice versa: the
 *    projects mint different audiences, so each backend rejects the other's
 *    tokens outright. The boundary is cryptographic, not a role check.
 *  - Both apps can be signed in simultaneously in one browser without either
 *    session disturbing the other, because Firebase scopes persistence per app.
 */

import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import {
  signInWithPopup,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  updateProfile,
  signOut,
  onIdTokenChanged,
  type Auth,
  type User,
} from 'firebase/auth';
import { startAuth } from './startAuth';

const APP_NAME = 'learn';

/**
 * The learner project's web config, as a compile-time default.
 *
 * This is deliberate, not a leak. A Firebase web config is public by design —
 * it identifies the project, it does not authorise anything; access is governed
 * by Firebase security rules and the authorised-domains list, and these exact
 * values already ship inside every bundle we serve and sit in the deploy
 * scripts in this repo.
 *
 * It is here because the alternative cost us two production outages. When the
 * config came only from build-time env, any build that did not pass
 * VITE_LEARN_FIREBASE_* still compiled, still deployed, and still rendered —
 * it just told every learner "sign-in is not configured". A silent login outage
 * that looks like a healthy deploy. Several sessions deploy this app from their
 * own checkouts, so a guard in one deploy script cannot prevent it; making the
 * config impossible to omit can.
 *
 * The env vars still win when set, so a different project can be targeted
 * without touching this file.
 */
const LEARN_FIREBASE_DEFAULTS = {
  apiKey: 'AIzaSyDRDF82s9leXrZ9e6sVOJmyqc7BxVKulrg',
  authDomain: 'learn.4klabs.ai',
  projectId: 'fourklabs-learn',
  storageBucket: 'fourklabs-learn.firebasestorage.app',
  messagingSenderId: '150400868325',
  appId: '1:150400868325:web:5e3fd327c574fe5f2e190c',
} as const;

export const learnFirebaseConfig = {
  apiKey: import.meta.env.VITE_LEARN_FIREBASE_API_KEY || LEARN_FIREBASE_DEFAULTS.apiKey,
  authDomain: import.meta.env.VITE_LEARN_FIREBASE_AUTH_DOMAIN || LEARN_FIREBASE_DEFAULTS.authDomain,
  projectId: import.meta.env.VITE_LEARN_FIREBASE_PROJECT_ID || LEARN_FIREBASE_DEFAULTS.projectId,
  storageBucket: import.meta.env.VITE_LEARN_FIREBASE_STORAGE_BUCKET || LEARN_FIREBASE_DEFAULTS.storageBucket,
  messagingSenderId:
    import.meta.env.VITE_LEARN_FIREBASE_MESSAGING_SENDER_ID || LEARN_FIREBASE_DEFAULTS.messagingSenderId,
  appId: import.meta.env.VITE_LEARN_FIREBASE_APP_ID || LEARN_FIREBASE_DEFAULTS.appId,
};

export const isLearnAuthConfigured = Boolean(
  learnFirebaseConfig.apiKey && learnFirebaseConfig.authDomain && learnFirebaseConfig.projectId
);

let cachedAuth: Auth | null = null;

function learnApp(): FirebaseApp {
  const existing = getApps().find((a) => a.name === APP_NAME);
  return existing ?? initializeApp(learnFirebaseConfig, APP_NAME);
}

export function learnAuth(): Auth {
  if (cachedAuth) return cachedAuth;
  if (!isLearnAuthConfigured) {
    throw new Error('Learn sign-in is not configured (missing VITE_LEARN_FIREBASE_* env vars).');
  }
  // Keeps learners signed in across visits, on localStorage (see startAuth).
  cachedAuth = startAuth(learnApp());
  return cachedAuth;
}

function googleProvider(): GoogleAuthProvider {
  const provider = new GoogleAuthProvider();
  provider.addScope('email');
  provider.addScope('profile');
  // Always let the learner choose which Google account — shared devices are
  // common in this audience, and a silent re-auth into the wrong account writes
  // one person's progress onto another's record.
  provider.setCustomParameters({ prompt: 'select_account' });
  return provider;
}

export async function signInLearnerWithGoogle(): Promise<User> {
  const { user } = await signInWithPopup(learnAuth(), googleProvider());
  return user;
}

export async function signInLearnerWithEmail(email: string, password: string): Promise<User> {
  const { user } = await signInWithEmailAndPassword(learnAuth(), email.trim(), password);
  return user;
}

export async function registerLearnerWithEmail(
  email: string,
  password: string,
  displayName?: string
): Promise<User> {
  const { user } = await createUserWithEmailAndPassword(learnAuth(), email.trim(), password);
  if (displayName?.trim()) {
    await updateProfile(user, { displayName: displayName.trim() }).catch(() => {
      /* cosmetic only — the account exists either way */
    });
  }
  await sendEmailVerification(user).catch(() => {
    /* the verify screen offers a resend */
  });
  return user;
}

export async function resendLearnerVerification(): Promise<void> {
  const user = learnAuth().currentUser;
  if (user) await sendEmailVerification(user);
}

export async function sendLearnerPasswordReset(email: string): Promise<void> {
  await sendPasswordResetEmail(learnAuth(), email.trim());
}

export async function signOutLearner(): Promise<void> {
  if (!isLearnAuthConfigured) return;
  await signOut(learnAuth());
}

/** Fresh ID token for API calls; null when signed out. */
export async function getLearnIdToken(forceRefresh = false): Promise<string | null> {
  if (!isLearnAuthConfigured) return null;
  const user = learnAuth().currentUser;
  return user ? user.getIdToken(forceRefresh) : null;
}

export function onLearnAuthChanged(cb: (user: User | null) => void): () => void {
  if (!isLearnAuthConfigured) {
    cb(null);
    return () => {};
  }
  // onIdTokenChanged (not onAuthStateChanged) so a token refresh — including
  // the one right after email verification — re-renders the guard.
  return onIdTokenChanged(learnAuth(), cb);
}

/** Human-readable message for the Firebase error codes learners actually hit. */
export function learnAuthErrorMessage(error: unknown): string {
  const code = (error as { code?: string })?.code ?? '';
  switch (code) {
    case 'auth/invalid-email':
      return 'That email address does not look right.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Email or password is incorrect.';
    case 'auth/email-already-in-use':
      return 'An account already exists for this email. Sign in instead.';
    case 'auth/weak-password':
      return 'Use a password of at least 6 characters.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a minute and try again.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'Sign-in was cancelled.';
    case 'auth/popup-blocked':
      return 'Your browser blocked the sign-in popup. Allow popups and try again.';
    case 'auth/network-request-failed':
      return 'Network problem. Check your connection and try again.';
    default:
      return 'Could not sign you in. Try again.';
  }
}
